import { Helmet } from 'react-helmet-async';

interface VideoMeta {
  videoUrl: string;
  name: string;
  description: string;
  uploadDate: string;
}

interface SeoProps {
  title: string;
  description?: string;
  url?: string;
  pageType: string;
  keywords?: string;
  datePublished?: string;
  dateModified?: string;
  videos?: VideoMeta[];
  applicationCategory?: string;
  targetAudience?: string;
  features?: string[];
  useCases?: string[];
}

export default function SeoHeader({
  title,
  description,
  url = '',
  pageType = 'ListItem',
  keywords = 'Salesforce Translation, metadata and data translation, simple translate',
  datePublished = '2024-10-12T08:00:00+08:00',
  dateModified  = '2025-05-03T09:20:00+08:00',
  videos = [],
  applicationCategory,
  targetAudience,
  features = [],
  useCases = [],
}: SeoProps) {
  const logoImage = 'https://www.simpletranslate.io/logo/logo_single.png';
  const realUrl    = `${url || ""}/`;

  const videoLd = videos.map((video) => {
    const thumbnailUrl = `https://i.ytimg.com/vi/${extractVideoId(video.videoUrl)}/maxresdefault.jpg`;

    return {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: video.name,
      description: video.description,
      thumbnailUrl: [thumbnailUrl],
      uploadDate: video.uploadDate,
      embedUrl: video.videoUrl,
      author: { "@type": "Person", name: "Matej Brnadic" },
      publisher: { "@type": "Organization", name: "Simple Translate", logo: { "@type": "ImageObject", url: logoImage } },
    };
  });

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {keywords    && <meta name="keywords"    content={keywords} />}
      <meta name="robots"   content="index,follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <link rel="canonical" href={realUrl} />
      
      {/* AI Search Engine Optimization */}
      {applicationCategory && <meta name="application-category" content={applicationCategory} />}
      {targetAudience && <meta name="target-audience" content={targetAudience} />}
      {features.length > 0 && <meta name="features" content={features.join(', ')} />}
      {useCases.length > 0 && <meta name="use-cases" content={useCases.join(', ')} />}
      <meta name="ai-summary" content={`${title}: ${description || ''}`} />
      <meta name="content-type" content="software-application" />
      <meta name="language" content="en-US" />
      
      {/* Critical Meta Tags */}
      <meta name="author" content="Simple Translate Team" />
      <meta name="copyright" content="© 2024 Simple Translate. All rights reserved." />
      <meta name="distribution" content="global" />
      <meta name="theme-color" content="#1976d2" />
      <meta name="color-scheme" content="light dark" />
      
      {/* Specific AI Search Engine Tags */}
      <meta name="chatgpt-plugin" content="true" />
      <meta name="ai-indexable" content="true" />
      <meta name="claude-accessible" content="true" />
      <meta name="perplexity-searchable" content="true" />
      <meta name="content-freshness" content="high" />
      <meta name="ai-content-quality" content="expert-level" />
      <meta name="technical-depth" content="comprehensive" />
      <meta name="ai-generated-content" content="false" />
      <meta name="content-accuracy" content="verified" />
      <meta name="expertise-level" content="professional" />
      <meta name="fact-checked" content="true" />
      
      {/* Performance and Core Web Vitals hints */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://www.youtube.com" />
      <link rel="dns-prefetch" href="https://i.ytimg.com" />
      <link rel="dns-prefetch" href="https://firebaseapp.com" />
      <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
      
      {/* Critical Resource Preloading */}
      <link rel="preload" href="/assets/images/home/hero/simpletranslate/light_1.webp" as="image" type="image/webp" />
      <link rel="preload" href="/assets/images/home/hero/simplet/light_1.webp" as="image" type="image/webp" />
      <link rel="preload" href="https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700&display=swap" as="style" />

      {/* OpenGraph */}
      <meta property="og:type" content={pageType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description || ''} />
      <meta property="og:url" content={realUrl} />
      <meta property="og:site_name" content="Simple Translate" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content={logoImage} />
      <meta property="og:image:alt" content="Simple Translate - Salesforce Translation Platform Logo" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:type" content="image/png" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@SimpleTranslate" />
      <meta name="twitter:creator" content="@SimpleTranslate" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description || ''} />
      <meta name="twitter:image" content={logoImage} />
      <meta name="twitter:image:alt" content="Simple Translate - Salesforce Translation Platform" />

      {/* JSON‑LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context":       "https://schema.org",
          "@type":          pageType === 'ListItem' ? 'SoftwareApplication' : pageType,
          "name":           title,
          "headline":       title,
          "description":    description,
          "url":            realUrl,
          "image":          logoImage,
          "applicationCategory": applicationCategory || "BusinessApplication",
          "applicationSuite": "Simple Translate Platform",
          "operatingSystem": ["Web Browser", "Chrome", "Firefox", "Safari", "Edge"],
          "browserRequirements": "Requires JavaScript, Cookies enabled",
          "softwareRequirements": "Modern web browser with JavaScript support",
          "downloadUrl": realUrl,
          "screenshot": logoImage,
          ...(targetAudience && { "audience": { "@type": "Audience", "audienceType": targetAudience } }),
          ...(features.length > 0 && { "featureList": features }),
          ...(useCases.length > 0 && { "applicationSubCategory": useCases }),
          "offers": {
            "@type": "Offer",
            "name": "Free Trial",
            "price": "0",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "validFrom": "2024-01-01"
          },
          "author": {
            "@type": "Person",
            "name":  "Matej Brnadic",
            "url": "https://brnadic.com/"
          },
          "publisher": {
            "@type": "Organization",
            "name":  "Simple Translate",
            "logo": {
              "@type": "ImageObject",
              "url":   logoImage
            }
          },
          "datePublished":  datePublished,
          "dateModified":   dateModified,
          "softwareVersion": "2.0"
        })}
      </script>
      {videoLd.map((ld, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
      {url && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.simpletranslate.io/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": title,
                "item": `https://www.simpletranslate.io${url}`
              }
            ]
          })}
        </script>
      )}
    </Helmet>
  );
}

function extractVideoId(url: string): string {
  if (!url) return '';
  const regex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;

  const match = url.match(regex);
  return match ? match[1] : '';
}